import React, { useState } from 'react';
import SEO from './common/SEO';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useSendContactMessageMutation } from '@/features/api/contactApi';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [statusMessage, setStatusMessage] = useState('');
  const [sendMessage, { isLoading }] = useSendContactMessageMutation();
  const [showPopup, setShowPopup] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage('');

    const { name, email, subject, message } = formData;
    if (!name || !email || !subject || !message) {
      setStatusMessage('Please fill in all required fields.');
      return;
    }

    try {
      await sendMessage(formData).unwrap();
      setStatusMessage('Your message has been sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setShowPopup(true);
    } catch (err) {
      console.error('Contact form error:', err);
      if (err?.data?.message) {
        setStatusMessage(`Server error: ${err.data.message}`);
      } else if (err.error) {
        setStatusMessage(`Error: ${err.error}`);
      } else {
        setStatusMessage('An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <>
      <SEO title="Contact Us" />

      {/* Contact Section */}
      <div className="bg-white relative py-16 px-4">
        <div className="mx-auto w-full max-w-[1692px] flex flex-col items-center justify-center">
          {/* Section Title */}
          <div className="text-center mb-10">
            <span className="pre-title text-primary text-lg">Need Help?</span>
            <h1 className="title text-4xl font-semibold">Get In Touch With Us</h1>
          </div>

          {/* Form Container */}
          <div className="w-full flex justify-center">
            <Card className="w-full max-w-xl shadow-lg">
              <CardHeader>
                <CardTitle className="text-center">Contact Us</CardTitle>
                <CardDescription className="text-center">
                  We'd love to hear from you! Fill out the form below.
                </CardDescription>
              </CardHeader>

              <form onSubmit={handleSubmit} className="space-y-6">
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Jane Smith"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jane@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                                <Label htmlFor="subject">Subject</Label>
                                <select
                                  id="subject"
                                  name="subject"
                                  value={formData.subject}
                                  onChange={handleChange}
                                  required
                                  className="w-full p-3 border rounded-md bg-white"
                                >
                                  <option value="">Select a Subject</option>
                                  <option value="course">Course Related</option>
                                  <option value="studyabroad">Study Abroad</option>
                                  <option value="website">Other</option>
                                </select>
                              </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      required
                    />
                  </div>
                </CardContent>

                <CardFooter>
                  <Button
                    type="submit"
                    className="w-full text-white bg-green-600 hover:bg-green-700"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </Button>
                </CardFooter>
              </form>

              {statusMessage && (
                <div
                  className={`p-4 text-center text-sm font-medium ${
                    statusMessage.toLowerCase().includes('error') ||
                    statusMessage.toLowerCase().includes('please')
                      ? 'text-red-600'
                      : 'text-green-600'
                  }`}
                >
                  {statusMessage}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Shapes */}
        <img className="absolute bottom-20 left-5 z-[-1]" src="/image/shapes/shape-04-01.png" alt="Shape Top Left" />
        <img className="absolute -bottom-12 right-5 z-[-1]" src="/image/shapes/shape-02-08.png" alt="Shape Bottom Right" />
        <img className="absolute top-5 left-2 z-[-1]" src="/image/shapes/shape-15.png" alt="Shape Center" />
      </div>

      {/* Google Map */}
      <div className="mt-20 px-4 w-full max-w-[1692px] mx-auto">
        <div className="google-map">
          <iframe
            title="Google Map"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3719.170096058674!2d72.7963913149506!3d21.145244785925142!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04f45b61c9ad1%3A0x6d3dd9cd92d3fd0c!2sForeign%20English%20Tests%20Capital%20-%20FETC!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
            width="100%"
            height="500"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>
      </div>

      {/* Success Popup */}
      {showPopup && (
        <div className="fixed inset-0 flex justify-center items-center bg-gray-500 bg-opacity-75 z-50">
          <Card className="max-w-sm p-6 bg-white shadow-lg rounded-md">
            <CardHeader>
              <CardTitle className="text-center text-lg font-semibold">Thank You!</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p>Your message has been received. We’ll get back to you shortly!</p>
            </CardContent>
            <CardFooter>
              <Button className="w-full bg-green-600 text-white" onClick={() => setShowPopup(false)}>
                Close
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </>
  );
};

export default ContactUs;
